import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { Owner, OwnerFilter } from "../types";
import { readReportingOwner } from "../lib/reporting";
import { api } from "../lib/api";
import { clearOfflineProfile, readOfflineProfile, saveOfflineProfile } from "../lib/offline";

/** Who is signed in. Asks the server once on load; `checking` is true until it has answered. */
export function useSession({ setOwner, setError }: {
  setOwner: Dispatch<SetStateAction<OwnerFilter>>;
  setError: (message: string) => void;
}) {
  const [profile, setProfile] = useState<Owner | null>(null);
  const [checking, setChecking] = useState(true);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    api<{ profile: Owner }>("session")
      .then((s) => {
        if (mounted.current) {
          saveOfflineProfile(s.profile);
          setProfile(s.profile);
          setOwner(readReportingOwner(new URLSearchParams(window.location.search), s.profile));
        }
      })
      .catch((e) => {
        if ((e as { status?: number }).status === 401) {
          clearOfflineProfile();
        } else {
          const cached = readOfflineProfile();
          if (cached && mounted.current) {
            setProfile(cached);
            setOwner(readReportingOwner(new URLSearchParams(window.location.search), cached));
          } else {
            setError((e as Error).message);
          }
        }
      })
      .finally(() => {
        if (mounted.current) setChecking(false);
      });
    return () => {
      mounted.current = false;
    };
  // Checks the session once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { profile, setProfile, checking };
}
